import { useMemo } from "react"
import { Tooltip } from "antd"
import { Select } from "components/ui/select/Select"
import { formatDeliveryDateLabel } from "helpers/utils.helper"
import { defaultTooltipStyles, getShadowTooltipContainer } from "components/ui/customWebviewer/shared/shadowTooltip"
import type { TFurnitureDeliveryDate } from "types/elevationReferences.type"
import style from "./DeliveryDateSection.module.scss"

const { wrapper, labelRow, label, inheritedTag, selectMuted, resetButton } = style

const INHERITED_TOOLTIP = "The delivery date and project are inherited from the connected elevation reference."

// The sidebar is user-resizable and can get narrow, so cap the bubble width and let the sentence
// wrap instead of overflowing the panel's edge.
const inheritedTooltipStyles = {
	body: { ...defaultTooltipStyles.body, maxWidth: 220 },
}

type TDeliveryDateOption = { id: number; date: string; project_name?: string | null }

type Props = {
	deliveryDates: TDeliveryDateOption[]
	value: TFurnitureDeliveryDate | null
	/**
	 * Whether "Inherit from elevation" has somewhere to send the object back to — is it connected to
	 * an eref at all. With none there is nothing to inherit from. Only consulted when `canReset` is
	 * omitted; see that prop for the batch panel's own, richer condition.
	 */
	hasEref: boolean
	onChange: (id: number | null) => void
	onReset: () => void
	/**
	 * Overrides the Select's empty-state text. The batch furniture panel (#1390) reuses this section
	 * verbatim and passes "Mixed" here when the selected objects' dates disagree — same shape
	 * `ElevationReferenceSelect` already has for its own Mixed case. Defaults to the single-object
	 * wording, so that caller is unaffected.
	 */
	placeholder?: string
	/**
	 * Overrides whether "Inherit from elevation" is offered, instead of deriving it from `value` and
	 * `hasEref`. The single-object caller omits this and keeps the original derivation.
	 *
	 * The batch panel (#1390) needs its own answer: the reset is still a single, well-defined action
	 * — "clear every selected object's override" — even when the selection's CURRENTLY DISPLAYED
	 * dates are Mixed (say, one object already carries a different override). What decides whether it
	 * is SAFE is `canResetBatchDeliveryDate` (every object has an eref, and those elevations resolve
	 * to the same date) — not whether today's values happen to already agree. Hiding it just because
	 * the display is Mixed would be an arbitrary restriction with no backend reason behind it.
	 */
	canReset?: boolean
}

export const DeliveryDateSection = ({
	deliveryDates,
	value,
	hasEref,
	onChange,
	onReset,
	placeholder = "Select delivery date",
	canReset,
}: Props) => {
	const options = useMemo(
		() => deliveryDates.map((d) => ({ label: formatDeliveryDateLabel(d.date, d.project_name), value: d.id })),
		[deliveryDates],
	)

	const isInherited = value?.is_inherited ?? false
	const showReset = canReset ?? (!isInherited && value !== null && hasEref)

	return (
		<div className={wrapper}>
			<div className={labelRow}>
				<span className={label}>Delivery date and project</span>
				{isInherited ? (
					<Tooltip
						title={INHERITED_TOOLTIP}
						placement="top"
						getPopupContainer={getShadowTooltipContainer}
						styles={inheritedTooltipStyles}
					>
						<span className={inheritedTag}>Inherited</span>
					</Tooltip>
				) : null}
			</div>
			<Select
				variant="outlined"
				options={options}
				value={value?.id ?? null}
				// `onSelect`, not `onChange`: when the value is inherited, the shown id can already
				// equal an option's id, so antd's `onChange` (fires only on an actual value change)
				// would silently no-op on that option. `onSelect` fires on every option click.
				onSelect={(next) => onChange(typeof next === "number" ? next : null)}
				placeholder={placeholder}
				className={isInherited ? selectMuted : undefined}
				showSearch
				optionFilterProp="label"
				renderInPlace
			/>
			{showReset ? (
				<button type="button" className={resetButton} onClick={onReset}>
					Inherit from elevation
				</button>
			) : null}
		</div>
	)
}
